// Libraries
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { client, GET_SEGMENT_TABLE_TREE, GET_TABLES, useQuery } from 'utils/graphql';

const TableTreeContext = createContext();

export const TableTreeProvider = ({ children }) => {
    const [tableTrees, setTableTrees] = useState({});
    const isLoading = useRef(false);

    const { data: tablesData } = useQuery(GET_TABLES, {
        variables: {
            offset: 0,
            limit: 99,
        },
        fetchPolicy: 'cache-and-network',
    });

    const loadTableTree = async (id) => {
        try {
            const result = await client.query({
                query: GET_SEGMENT_TABLE_TREE,
                variables: { tableId: id },
                fetchPolicy: 'network-only',
            });
            const tree = result.data.segmentationTableTree;
            if (tree) {
                setTableTrees((prev) => ({
                    ...prev,
                    [tree.table.global_id]: tree,
                }));
            }
            return tree;
        } catch (err) {
            console.error(`Error loading table tree ${id}:`, err);
        }
    };

    useEffect(() => {
        if (!tablesData || isLoading.current) return;

        const loadInBatches = async (tableIds, batchSize = 3) => {
            for (let i = 0; i < tableIds.length; i += batchSize) {
                const batch = tableIds.slice(i, i + batchSize);
                await Promise.all(batch.map((id) => loadTableTree(id)));
                // delay between batches to yield to UI / network
                await new Promise((res) => setTimeout(res, 100));
            }
        };
        const tables = tablesData.tableSchemaList.edges
            .filter((edge) => !edge.node.isJunction)
            .map((edge) => edge.node);
        const tableIds = tables.map((table) => table.id).filter((id) => !tableTrees[id]);
        if (tableIds.length > 0) {
            isLoading.current = true;
            // loading table trees in batchs of 3
            loadInBatches(tableIds, 3);
        }
    }, [tablesData, tableTrees]);

    const getTableTreeByGlobalId = async (globalId) => {
        if (!tableTrees[globalId]) {
            return await loadTableTree(globalId);
        }
        return tableTrees[globalId] || null;
    };

    const getTableTreeById = async (id) => {
        // check dictionary cache first
        const existingTree = Object.values(tableTrees).find((tree) => tree.table.id === id);
        if (existingTree) {
            return existingTree;
        }
        return await loadTableTree(id);
    };

    return (
        <TableTreeContext.Provider value={{ tableTrees, getTableTreeByGlobalId, getTableTreeById }}>
            {children}
        </TableTreeContext.Provider>
    );
};

export const useTableTrees = () => useContext(TableTreeContext);
